/**
 * Production-Ready Error Reporting Helper V2
 * 
 * Features:
 * - Uses window.ErrorMonitor (properly attached)
 * - beforeSend callback support
 * - Validation and error handling
 * - TypeScript with proper types
 */

import { errorMonitorV2, type ErrorReport, type ObservabilityCallbacks } from './error-monitoring-v2'
import type { ErrorCategory } from './error-types'

export interface ErrorContext {
  component: string
  operation: string
  storyId?: string
  severity?: 'critical' | 'high' | 'medium' | 'low'
  category?: string
}

export interface ReportErrorOptions {
  beforeSend?: (error: Error, context: ErrorContext) => Error | null
  callbacks?: ObservabilityCallbacks
}

/**
 * Reports an error to the error monitoring system
 * Uses direct import with fallback to window attachment
 */
export function reportError(
  error: unknown, 
  context: ErrorContext, 
  options?: ReportErrorOptions
): void {
  if (typeof window === 'undefined') {
    ;(async () => {
      try {
        const { reportError: reportServerError } = await import('@/lib/server-error-monitoring')

        const severity = context.severity ?? 'medium'

        await reportServerError(error instanceof Error ? error : String(error), {
          category: (context.category || 'api_error') as ErrorCategory,
          severity: severity === 'low' || severity === 'medium' || severity === 'high' || severity === 'critical'
            ? severity
            : 'medium',
          apiEndpoint: context.operation,
          operation: context.operation,
          storyId: context.storyId
        })
      } catch (serverReportingError) {
        console.error('Failed to report server-side error:', serverReportingError)
      }
    })()

    return
  }
  
  // Check if error monitoring is ready
  if (!errorMonitorV2.isReady()) {
    console.warn('Error monitoring not ready, logging to console:', error)
    return
  }
  
  try {
    // Prepare error object
    let errorObj: Error
    if (error instanceof Error) {
      errorObj = error
    } else {
      errorObj = new Error(String(error))
    }

    // Apply beforeSend callback if provided
    if (options?.beforeSend) {
      const processedError = options.beforeSend(errorObj, context)
      if (!processedError) {
        console.debug('Error dropped by beforeSend callback')
        return
      }
      errorObj = processedError
    }

    // Create error report
    const errorReport: Omit<ErrorReport, 'timestamp' | 'session_id' | 'user_agent' | 'story_id'> = {
      message: errorObj.message,
      ...(errorObj.stack && { stack: errorObj.stack }),
      category: (context.category || 'api_error') as ErrorCategory,
      severity: context.severity || 'medium',
      source: 'client',
      url: window.location.href,
      component: context.component,
      operation: context.operation,
      ...(context.storyId && { story_id: context.storyId })
    }

    // Send to error monitor
    errorMonitorV2.captureError(errorReport)
    
  } catch (reportingError) {
    // Don't let error reporting itself cause errors
    console.error('Failed to report error:', reportingError)
  }
}

/**
 * Convenience function for API errors
 */
export function reportApiError(
  error: unknown, 
  context: {
    component: string
    operation: string
    storyId?: string
    severity?: 'critical' | 'high' | 'medium' | 'low'
  },
  options?: ReportErrorOptions
): void {
  reportError(error, {
    ...context,
    category: 'api_error'
  }, options)
}

/**
 * Convenience function for critical story operations
 */
export function reportCriticalError(
  error: unknown, 
  context: {
    component: string
    operation: string
    storyId?: string
  },
  options?: ReportErrorOptions
): void {
  reportError(error, {
    ...context,
    category: 'api_error',
    severity: 'critical'
  }, options)
}

/**
 * Get error monitoring status
 */
export function getErrorMonitoringStatus() {
  return errorMonitorV2.getStatus()
}

/**
 * Initialize error monitoring with callbacks
 */
export function initErrorMonitoring(callbacks?: ObservabilityCallbacks): void {
  errorMonitorV2.init(callbacks)
}

/**
 * Destroy error monitoring
 */
export function destroyErrorMonitoring(): void {
  errorMonitorV2.destroy()
}

/**
 * Set user context
 */
export function setErrorMonitoringUser(userId: string, userTier?: 'basic' | 'premium'): void {
  errorMonitorV2.setUser(userId, userTier)
}

/**
 * Set story context
 */
export function setErrorMonitoringStory(storyId: string): void {
  errorMonitorV2.setStoryId(storyId)
}

/**
 * Process queued errors (useful for testing or manual processing)
 */
export function processQueuedErrors(): void {
  errorMonitorV2.processQueuedErrors()
}

/**
 * Example usage:
 * 
 * ```typescript
 * import { reportError, reportApiError, reportCriticalError } from '@/lib/report-error-v2'
 * 
 * // Basic error reporting
 * try {
 *   await fetch('/api/data')
 * } catch (error) {
 *   reportError(error, {
 *     component: 'DataFetcher',
 *     operation: 'fetch_data',
 *     storyId: 'story-123',
 *     severity: 'high'
 *   })
 * }
 * 
 * // API error with beforeSend callback
 * try {
 *   await apiCall()
 * } catch (error) {
 *   reportApiError(error, {
 *     component: 'ApiClient',
 *     operation: 'api_call',
 *     storyId: 'story-123'
 *   }, {
 *     beforeSend: (error, context) => {
 *       // Filter out specific errors
 *       if (error.message.includes('Network Error')) {
 *         return null // Drop this error
 *       }
 *       return error // Send this error
 *     }
 *   })
 * }
 * 
 * // Critical error
 * try {
 *   await criticalOperation()
 * } catch (error) {
 *   reportCriticalError(error, {
 *     component: 'CriticalSystem',
 *     operation: 'critical_operation',
 *     storyId: 'story-123'
 *   })
 * }
 * ```
 */
